import { useNavigate } from "react-router";

import { AuthWrapper } from "@/lib/aws-amplify";
import { isAuthError } from "@/lib/aws-amplify/auth";
import type { CognitoAccountDuplicateRequestMetadata } from "@/lib/gql/__generated__/graphql";

import { DevelopmentTools } from "@/hooks/use-development-tools";

import { QUERY_PARAMS } from "@/routes/_constants/query-params";
import { PATHS } from "@/routes/paths";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info/_hooks/use-toast";
import { useLoadingScreen } from "@/components/features/side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";

import { useAuth } from "@/providers";

import { useSignOutSilently } from "../use-sign-out-silently";

/**
 * invitation sign up 時にユーザーから入力を受け取るパラメーター
 */
export type InvitationSignUpParams = {
	email: string;
	password: string;
	clientMetadata: Omit<CognitoAccountDuplicateRequestMetadata, "callbackUrl" | "codeParameter">;
};

/**
 * invitation sign up 時に副作用として発生するパラメーター
 * @param email
 * @returns
 */
const getCallbackUrlAndCodeParameter = (
	email: string,
	companyID: string,
): Pick<CognitoAccountDuplicateRequestMetadata, "callbackUrl" | "codeParameter"> => {
	const origin = window.location.origin;
	const codeParameter = "{####}";
	const callbackUrl = `${origin}${PATHS.AUTH.INVITATION.VERIFY}?${QUERY_PARAMS.AUTH.INVITATION_VERIFY.USERNAME}=${encodeURIComponent(email)}&${QUERY_PARAMS.AUTH.INVITATION_VERIFY.COMPANY_ID}=${companyID}&${QUERY_PARAMS.AUTH.INVITATION_VERIFY.VERIFY_CODE}=${codeParameter}`;
	return {
		callbackUrl,
		codeParameter,
	};
};

export const useInvitationSignUp = () => {
	const { isSignedIn } = useAuth();
	const { signOutSilently } = useSignOutSilently();
	const { consoleErrorOnlyDevMode } = DevelopmentTools();
	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();

	const navigate = useNavigate();

	const invitationSignUp = async ({ email, password, clientMetadata: _clientMetadata }: InvitationSignUpParams) => {
		showLoadingScreenCallback();

		const { callbackUrl, codeParameter } = getCallbackUrlAndCodeParameter(email, _clientMetadata.companyID);
		const clientMetadata: CognitoAccountDuplicateRequestMetadata = {
			..._clientMetadata,
			callbackUrl,
			codeParameter,
		};

		try {
			if (isSignedIn) {
				// サインインしている場合はサインアウトしてからアカウント作成処理を行う
				await signOutSilently();
			}

			await AuthWrapper.authInvitationSignUp(email, password, clientMetadata);
			navigate(PATHS.AUTH.VERIFY_YOUR_EMAIL);
		} catch (err) {
			if (isAuthError(err)) {
				/**
				 * @see {@link(https://docs.aws.amazon.com/ja_jp/cognito-user-identity-pools/latest/APIReference/CommonErrors.html)}
				 * 内部のエラーに関しての case は除ている
				 */
				switch (err.name) {
					case "AliasExistsException":
						/**
						 * This exception is thrown when a user tries to confirm the account with an email address or phone number that has already been supplied as an alias for a different user profile.
						 * This exception indicates that an account with this email address or phone already exists in a user pool that you've configured to use email address or phone number as a sign-in alias.
						 */
						notifyUserEventInfo({
							variant: "error",
							title: "無効なメールアドレスです",
							description: "別のメールアドレスをご利用ください",
						});
						navigate(PATHS.AUTH.SIGN_IN);

						break;
					case "CodeMismatchException":
						/**
						 * This exception is thrown if the provided code doesn't match what the server was expecting.
						 */
						break;
					case "ExpiredCodeException":
						/**
						 * This exception is thrown if a code has expired.
						 */
						break;
					case "InvalidParameterException":
						/**
						 * This exception is thrown when the Amazon Cognito service encounters an invalid parameter.
						 */
						break;
					case "LimitExceededException":
						/**
						 * This exception is thrown when a user exceeds the limit for a requested Amazon Web Services resource.
						 */
						notifyUserEventInfo({
							variant: "error",
							title: "アカウント作成に失敗しました",
							description: "時間を空けて、もう一度お試しください",
						});
						navigate(PATHS.AUTH.SIGN_UP);

						break;
					case "NotAuthorizedException":
						/**
						 * This exception is thrown when a user isn't authorized.
						 */
						break;
					case "TooManyRequestsException":
						/**
						 * This exception is thrown when the user has made too many requests for a given operation.
						 */
						notifyUserEventInfo({
							variant: "error",
							title: "アカウント作成に失敗しました",
							description: "時間を空けて、もう一度お試しください",
						});
						navigate(PATHS.AUTH.SIGN_UP);

						break;
					case "UserNotFoundException":
						/**
						 * This exception is thrown when a user isn't found.
						 */
						break;
					case "UsernameExistsException":
						notifyUserEventInfo({
							variant: "error",
							title: "アカウント作成に失敗しました",
							description: "既に登録されているメールアドレスです。他のメールアドレスを設定してください。",
						});
						break;
					default:
						notifyUserEventInfo({
							variant: "error",
							title: "アカウント作成に失敗しました",
							description: "もう一度お試しください",
						});
						navigate(PATHS.AUTH.SIGN_UP);
				}
			}
			consoleErrorOnlyDevMode(err);
		} finally {
			hideLoadingScreenCallback();
		}
	};

	return {
		invitationSignUp,
	};
};
